import { Action, Selector, State, StateContext } from "@ngxs/store";
import { ReferralStateModel } from "./referral-model";
import { Injectable } from "@angular/core";
import { forkJoin, lastValueFrom, tap } from "rxjs";
import { append, iif, insertItem, patch, removeItem, updateItem } from "@ngxs/store/operators";
import { Referrals } from "./referral-actions";
import { ReferralService } from "../../services/referral.service";
import { TealCompReferral, TealCompReferralHeader } from "../../models/teal-comp-referral";
import { MessageService } from "primeng/api";
import { EncounterService } from "../../services/encounter.service";
import { TealCompEncounterDetails } from "../../models/encounter";
import { plainToClass } from "class-transformer";
import { Diary, Fax, Note, Users, ReceivedEmail, SentEmail, UserService } from "@harlowe/core"


@State<ReferralStateModel>
(
    {
        name: "referrals",
        defaults: 
        {
            loadedReferrals: [],
            selectedReferral: undefined,
            authorizingReferrals:[],
            creatingReferrals: [],
            recentlyClosedReferrals: [],
            unopenedReferralIds: []
            
        }
    }
)

  @Injectable()
  export class ReferralState 
  {

    constructor
    (
        private _referralService: ReferralService,
        private _messageService: MessageService,
        private _encounterService: EncounterService,
        private _userService: UserService
    )
    {

    }

    @Selector()
    static newReferrals(state: ReferralStateModel) 
    {
      return state.loadedReferrals.filter(s => s.status == 'New');
    }

    @Selector()
    static reviewedReferrals(state: ReferralStateModel) 
    {
      return state.loadedReferrals.filter(s => s.status == 'Reviewed');
    }

    @Selector()
    static submittedForApprovalReferrals(state: ReferralStateModel) 
    {
      return state.loadedReferrals.filter(s => s.status == 'SubmittedForApproval');
    }

    @Selector()
    static approvedReferrals(state: ReferralStateModel) 
    {
      return state.loadedReferrals.filter(s => s.status == 'Approved');
    }

    @Selector()
    static recentlyClosedReferrals(state: ReferralStateModel)
    {
        return state.recentlyClosedReferrals;
    }

    @Selector()
    static unopenedReferralIds(state: ReferralStateModel)
    {
        return state.unopenedReferralIds;
    }

    @Selector()
    static referrals(state: ReferralStateModel) : TealCompReferralHeader[]
    {
      return state.loadedReferrals;
    }

    @Selector()
    static currentReferral(state: ReferralStateModel) : TealCompReferral | undefined
    {
      return state.selectedReferral;
    }

    @Selector()
    static currentReferralEncounter(state: ReferralStateModel) : TealCompEncounterDetails | undefined
    {
      return state.selectedReferralEncounter;
    }

    @Selector()
    static referralIdsAuthorizing(state: ReferralStateModel ) : number[]
    {
        return state.authorizingReferrals.map(r => r.id);
    }
    @Selector()
    static referralIdsCreating(state: ReferralStateModel ) : number[]
    {
        return state.creatingReferrals.map(r => r.id);
    }


    @Action([Users.UserLoggedIn])
    async userLoggedIn(ctx: StateContext<ReferralStateModel>, action: Users.UserLoggedIn)
    {
        const state = ctx.getState();
        let referrals = await this._referralService.getActiveReferrals();
        var closedReferrals = await this._referralService.getRecentlyClosedReferrals();


        var unopenedReferralIds = await this._userService.getSetting("persistedReferrals") as number[] | undefined;
        if (unopenedReferralIds == undefined)
        {
            await this._userService.setSetting("persistedReferrals", []);
        }
 
        ctx.setState
        (
            {
                ...state,
                loadedReferrals: referrals,
                recentlyClosedReferrals: closedReferrals,
                unopenedReferralIds: unopenedReferralIds || []
            }
        );
        ctx.dispatch(new Referrals.InitialReferralsLoaded())
    }

    @Action([Referrals.NewReferralReceived])
    async referralReceived(ctx: StateContext<ReferralStateModel>, action: Referrals.NewReferralReceived)
    {
        const state = ctx.getState();
        ctx.setState
        (
            {
                ...state,
                loadedReferrals: [...state.loadedReferrals,action.payload.referral],
                unopenedReferralIds: [...state.unopenedReferralIds, action.payload.referral.id]
            }
        );
        await lastValueFrom(ctx.dispatch(new Referrals.PersistState()));
    }

    @Action([Referrals.ReferralSelected])
    async referralSelected(ctx: StateContext<ReferralStateModel>, action: Referrals.ReferralSelected)
    {
        const state = ctx.getState();

        if (state.unopenedReferralIds.includes(action.payload.referral.id))
        {
            ctx.setState
            (
                {
                    ...state,
                    unopenedReferralIds: state.unopenedReferralIds.filter(id => id != action.payload.referral.id) 

                }
            )

            await lastValueFrom(ctx.dispatch(new Referrals.PersistState()));
        }

        let referral = await this._referralService.getReferral(action.payload.referral.id);
        let encounter = (await lastValueFrom(this._encounterService.getEncounter(referral.encounterId))) as TealCompEncounterDetails;
        ctx.setState
        (
            {
                ...state,
                selectedReferral: referral,
            }
        );

        await lastValueFrom(forkJoin
        (
            [this._encounterService.getEncounterNotes(referral.encounterId),
            this._encounterService.getEncounterDiaries(referral.encounterId),
            this._encounterService.getEncounterSentEmails(referral.encounterId),
            this._encounterService.getEncounterReceivedEmails(referral.encounterId),
            this._encounterService.getEncounterFaxes(referral.encounterId),
            ]
        )
        .pipe
        (
            tap
            (
                val => 
                {
                    encounter.notes = val[0].map(n => plainToClass(Note, n));
                    encounter.diaries = val[1].map(d => plainToClass(Diary,d));
                    encounter.sentEmails = val[2].map(e => plainToClass(SentEmail, e));
                    encounter.receivedEmails = val[3].map(e => plainToClass(ReceivedEmail, e));
                    encounter.outboundFaxes = val[4].map(e => plainToClass(Fax, e));
                    console.log(encounter.outboundFaxes);
                    ctx.patchState
                    (
                        {
                            selectedReferralEncounter: encounter,
                        }
                    )
                }
            )
        ))
        
    }

    @Action([Referrals.ReferralUpdated, Referrals.AuthorizationCreatedForReferral])
    async referralUpdated(ctx: StateContext<ReferralStateModel>, action: Referrals.ReferralUpdated | Referrals.AuthorizationCreatedForReferral)
    {
        var newRef = action.payload.referral;
        ctx.setState
        (
            patch<ReferralStateModel>({
                loadedReferrals: updateItem<TealCompReferralHeader>(
                r => r.id === newRef.id, newRef
                )
            })
        ) 
    }

    @Action([Referrals.ReferralClosed])
    async referralClosed(ctx: StateContext<ReferralStateModel>, action: Referrals.ReferralClosed)
    {
        ctx.setState
        (
            patch
            (
            {
                loadedReferrals: updateItem(r => r.id === action.payload.referral.id, action.payload.referral),
                recentlyClosedReferrals: insertItem(action.payload.referral)
            }
        )
        )
    }

    @Action([Referrals.ReferralUpdateComplete])
    async referralUpdateCancelled(ctx: StateContext<ReferralStateModel>)
    {
        const state = ctx.getState();
        ctx.setState
        (
            {
                ...state,
                selectedReferral: undefined
            }
        )
    }

    @Action([Referrals.ReferralSubmittedForAuthorization, Referrals.ReferralSubmittedForReAuthorization])
    async referralSubmittedForAuthorization(ctx: StateContext<ReferralStateModel>,action: Referrals.ReferralSubmittedForAuthorization | Referrals.ReferralSubmittedForReAuthorization)
    {
        const state = ctx.getState();


        //Push the authorizing referral into the list
        ctx.setState
        (
            patch<ReferralStateModel>
            (
                {
                    authorizingReferrals: insertItem<TealCompReferral>
                    (
                        action.payload.referral
                    )
                }
            )
        );
    }

    @Action([Referrals.ReferralAuthSubmittedForCreation])
    async referralSubmittedForAuthCreation(ctx: StateContext<ReferralStateModel>,action: Referrals.ReferralAuthSubmittedForCreation)
    {
        const state = ctx.getState();


        //Push the authorizing referral into the list
        ctx.setState
        (
            patch<ReferralStateModel>
            (
                {
                    creatingReferrals: insertItem<TealCompReferral>
                    (
                        action.payload.referral
                    )
                }
            )
        );
    }


    @Action([Referrals.ReferralAuthCreated])
    async referralSubmittedFor(ctx: StateContext<ReferralStateModel>,action: Referrals.ReferralAuthCreated)
    {
        const state = ctx.getState();


        //Push the authorizing referral into the list
        ctx.setState
        (
            patch<ReferralStateModel>
            (
                {
                    creatingReferrals: removeItem<TealCompReferral>
                    (
                        r => r.id == action.payload.referral.id
                    )
                }
            )
        );
    }

    @Action([Referrals.ReferralAuthorizationStatus])
    async referralAuthorizationStatus(ctx: StateContext<ReferralStateModel>, action: Referrals.ReferralAuthorizationStatus)
    {
        ctx.setState
        (
            patch<ReferralStateModel>
            ({
                
                //Update the referral in our list of loaded referrals
                loadedReferrals: updateItem<TealCompReferralHeader>(
                r => r.id === action.payload.referral.id,
                action.payload.referral),

                //Remove this referral from the list of ones being authorized
                authorizingReferrals: removeItem<TealCompReferral>(r => r.id === action.payload.referral.id)
            })
        )                       
    }

    @Action ([Referrals.NoteAdded, Referrals.NoteUpdated])
    async encounterNoteAdded
    (
        ctx:StateContext<ReferralStateModel>,
        action:Referrals.NoteAdded | Referrals.NoteUpdated
    )
    {
        var existingState = ctx.getState();
        if(existingState.selectedReferral?.encounterId == action.payload.encounterId)
        {
            ctx.setState
            (
                patch<ReferralStateModel>
                ({
                    selectedReferralEncounter: patch
                    (
                        {
                            notes: iif( action instanceof Referrals.NoteAdded ,append([action.payload.note]), updateItem(n => n.id == action.payload.note.id, action.payload.note))
                        }
                    )
                })
            )

        }
    }

    @Action (Referrals.NoteDeleted)
    async encounterNoteDeleted
    (
        ctx:StateContext<ReferralStateModel>,
        action:Referrals.NoteDeleted
    )
    {
        var existingState = ctx.getState();
        if(existingState.selectedReferral?.encounterId == action.payload.encounterId)
        {
            ctx.setState
            (
                patch<ReferralStateModel>
                ({
                    selectedReferralEncounter: patch
                    (
                        {
                            notes: removeItem<Note>(note=>note.id === action.payload.noteId)
                        }
                    )
                })
            )

        }
    }

    @Action([Referrals.DiaryAdded, Referrals.DiaryUpdated])
    async encounterDiaryAdded
    (
        ctx:StateContext<ReferralStateModel>,
        action:Referrals.DiaryAdded | Referrals.DiaryUpdated
    )
    {
        var existingState = ctx.getState();
        if(existingState.selectedReferral?.encounterId == action.payload.encounterId)
        {
            ctx.setState
            (
                patch<ReferralStateModel>
                ({
                    selectedReferralEncounter: patch
                    (
                        {
                            diaries: iif( action instanceof Referrals.DiaryAdded ,append([action.payload.diary]), updateItem(n => n.id == action.payload.diary.id, action.payload.diary))
                        }
                    )
                })
            )

        }
    }




    @Action([Referrals.EncounterStateEncounterEmailUpdated ],{cancelUncompleted: true})
    async encounterEmailUpdated(ctx:StateContext<ReferralStateModel>,action:Referrals.EncounterStateEncounterEmailUpdated)
    {
        //See if active encounter owns this email
        var existingState =ctx.getState();
        if(existingState.selectedReferralEncounter && existingState.selectedReferralEncounter.sentEmails.map(e => e.id).includes(action.payload.email.id))
        {
            ctx.setState
            (
                patch<ReferralStateModel>
                ({
                    selectedReferralEncounter:patch
                    (
                        {
                            sentEmails: updateItem(e => e.id == action.payload.email.id, action.payload.email)
                        }
                    )
                })
            )
        }

    }

    @Action([Referrals.EncounterStateEncounterEmailSent ],{cancelUncompleted: true})
    async encounterEmailSent(ctx:StateContext<ReferralStateModel>,action:Referrals.EncounterStateEncounterEmailSent)
    {
        var existingState = ctx.getState();
        if(!existingState.selectedReferralEncounter?.sentEmails?.map(e => e.id).includes(action.payload.email.id))
        {
            ctx.setState
            (
                patch<ReferralStateModel>
                ({
                    selectedReferralEncounter: patch
                    (
                        {
                            sentEmails: append([action.payload.email])
                        }
                    )
                })
            )
        }

    }


    @Action([Referrals.EncounterStateEncounterEmailReceived ],{cancelUncompleted: true})
    async encounterEmailReceived(ctx:StateContext<ReferralStateModel>,action:Referrals.EncounterStateEncounterEmailReceived)
    {
        var existingState = ctx.getState();
        if(existingState.selectedReferralEncounter?.id == action.payload.encounterId && !existingState.selectedReferralEncounter?.receivedEmails.map(e => e.id).includes(action.payload.email.id))
        {
            ctx.setState
            (
                patch<ReferralStateModel>
                ({
                    selectedReferralEncounter: patch
                    (
                        {
                            receivedEmails: append([action.payload.email])
                        }
                    )
                })
            )

        }

    }


    @Action([Referrals.EncounterStateAttachmentDeleted])
    async encounterAttachmentDeleted(ctx:StateContext<ReferralStateModel>,action:Referrals.EncounterStateAttachmentDeleted)
    {
        var existingState = ctx.getState();
        if(existingState.selectedReferralEncounter?.attachments.map(a => a.id).includes(action.payload.file.id))
        {
            var index = existingState.selectedReferralEncounter.attachments.findIndex(a => a.id == action.payload.file.id)

            ctx.setState
            (
                patch<ReferralStateModel>
                ({
                    selectedReferralEncounter: patch
                    (
                        {
                            attachments: removeItem(index)
                        }
                    )
                })
            )
        }        
    }

    @Action([Referrals.EncounterStateAttachmentAdded])
    async encounterAttachmentAdded(ctx:StateContext<ReferralStateModel>,action:Referrals.EncounterStateAttachmentAdded)
    {
        var existingState = ctx.getState();
        if(!existingState.selectedReferralEncounter?.attachments.map(a => a.id).includes(action.payload.file.id))
        {

            ctx.setState
            (
                patch<ReferralStateModel>
                ({
                    selectedReferralEncounter: patch
                    (
                        {
                            attachments: append([action.payload.file])
                        }
                    )
                })
            )

        }
    }


    @Action([Referrals.EncounterStateAttachmentsAdded])
    async encounterAttachmentsAdded(ctx:StateContext<ReferralStateModel>,action:Referrals.EncounterStateAttachmentsAdded)
    {
        var existingState = ctx.getState();
        var existingIds = existingState.selectedReferralEncounter?.attachments.map(a => a.id);
        var attachmentsToAdd = action.payload.files.filter(a => !existingIds?.includes(a.id) )

        if(attachmentsToAdd && attachmentsToAdd.length > 0)
        {
            ctx.setState
            (
                patch<ReferralStateModel>
                ({
                    selectedReferralEncounter: patch
                    (
                        {
                            attachments: append(action.payload.files)
                        }
                    )
                })
            )
        }
    }

    @Action([Referrals.PersistState],{cancelUncompleted: true})
    async persistState(ctx:StateContext<ReferralStateModel>)
    {
        var state = ctx.getState();

        await this._userService.setSetting("persistedReferrals", state.unopenedReferralIds);
        //await lastValueFrom(ctx.dispatch(new UserInterface.UnBlockUI()));
    }

    // @Action([Referrals.InitialReferralsLoaded])
    // async initialMessagesLoaded(ctx: StateContext<ReferralStateModel>)
    // {
        
    //     //Update any that hadn't previously been delivered
    //     const state = ctx.getState();
    //     let undeliveredMessages = state.loadedReferrals.filter(m => !m.delivered);
    //     undeliveredMessages?.forEach
    //     (
    //         m =>
    //             this._messageService.markMessageAsDelivered(m)
    //     );
    // }

    // @Action([Messages.MessageDeliveryConfirmed])
    // async deliveryConfirmed(ctx: StateContext<MessageStateModel>, action:Messages.MessageDeliveryConfirmed)
    // {
    //     //replace the updated message in the state
    //     let message = action.payload.message;
    //     const state = ctx.getState();
        
        
    //     ctx.setState
    //     (
    //         patch<MessageStateModel>
    //         (
    //             {
    //                 loadedMessages: updateItem
    //                 (
    //                     item => item.id == message.id,
    //                     patch<ClientMessageHeader>({delivered:true})
                        
    //                 )
    //             }
    //         )
    //     )
    //     // let foundMessage = state.loadedMessages.find(m => m.id == message.id);
    //     // if(foundMessage)
    //     // {
    //     //     foundMessage.delivered = true;
    //     // }
    //             // ctx.setState
    //             // (
    //             //     {
    //             //         ...state,
    //             //         loadedMessages: [... state.loadedMessages.filter(m => m.id != message.id), message]  
    //             //     }
    //             // )
            
    // }

    
  }